import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, Send, X } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';
import { buildEmailCampaignCheckoutUrls, redirectToCampaignCheckout } from '../../lib/emailCampaignCheckout';

export type CampaignBillingRetryTarget = {
  id: string;
  name: string;
  recipient_count?: number | null;
  billing_status?: string | null;
  billing_payment_intent_id?: string | null;
  scheduled_at?: string | null;
  channel_type?: string | null;
};

interface CampaignBillingRetryModalProps {
  isOpen: boolean;
  businessId: string;
  campaign: CampaignBillingRetryTarget;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Retry charge (if needed) then send for email campaigns that failed billing
 * or failed after payment.
 */
export default function CampaignBillingRetryModal({
  isOpen,
  businessId,
  campaign,
  onClose,
  onSuccess,
}: CampaignBillingRetryModalProps) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const recipientCount = campaign.recipient_count || 0;
  const amount = recipientCount * 0.01;
  const alreadyPaid =
    campaign.billing_status === 'captured' && !!campaign.billing_payment_intent_id;
  const overdue =
    !!campaign.scheduled_at && new Date(campaign.scheduled_at).getTime() <= Date.now();

  const handleRetry = async () => {
    setProcessing(true);
    setError(null);
    try {
      let paymentIntentId = campaign.billing_payment_intent_id || '';

      if (!alreadyPaid) {
        const { successUrl, cancelUrl } = buildEmailCampaignCheckoutUrls(campaign.id);
        const { data: chargeResult, error: chargeError } = await supabase.functions.invoke(
          'charge-email-campaign',
          {
            body: {
              businessId,
              campaignId: campaign.id,
              recipientCount,
              successUrl,
              cancelUrl,
              intent: overdue ? 'send_now' : 'schedule',
            },
          },
        );

        if (redirectToCampaignCheckout(chargeResult)) {
          return;
        }

        if (chargeError || !chargeResult?.success || chargeResult?.status !== 'succeeded') {
          const msg =
            (await invokeErrorMessage(chargeError, chargeResult)) ||
            'Payment failed. Update your card in Subscription settings and try again.';
          // Edge marks billing_status failed; also mark campaign status failed for calendar.
          await supabase
            .from('email_campaigns')
            .update({ status: 'failed', billing_status: 'failed' })
            .eq('id', campaign.id);
          throw new Error(msg);
        }

        paymentIntentId = String(chargeResult.paymentIntentId || '').trim();
        if (!paymentIntentId) {
          await supabase
            .from('email_campaigns')
            .update({ status: 'failed', billing_status: 'failed' })
            .eq('id', campaign.id);
          throw new Error('Payment succeeded but no payment id was returned.');
        }
      }

      // Confirm row has captured payment before send.
      const { data: billed } = await supabase
        .from('email_campaigns')
        .select('billing_status, billing_payment_intent_id')
        .eq('id', campaign.id)
        .maybeSingle();

      if (billed?.billing_status !== 'captured' || !billed?.billing_payment_intent_id) {
        if (!paymentIntentId) {
          throw new Error('Campaign payment is not verified.');
        }
        await supabase
          .from('email_campaigns')
          .update({
            billing_status: 'captured',
            billing_payment_intent_id: paymentIntentId,
            billing_captured_at: new Date().toISOString(),
          })
          .eq('id', campaign.id);
      }

      await supabase
        .from('email_campaigns')
        .update({
          status: 'sending',
          send_immediately: true,
          // Clear schedule hold so it sends now after a missed scheduled attempt.
          ...(overdue ? { scheduled_at: null } : {}),
        })
        .eq('id', campaign.id);

      const { data: sendResult, error: sendError } = await supabase.functions.invoke(
        'send-email-campaign',
        {
          body: {
            campaignId: campaign.id,
            businessId,
            skipBilling: true,
          },
        },
      );

      if (sendError || !sendResult?.success) {
        const msg =
          (await invokeErrorMessage(sendError, sendResult)) ||
          'Failed to queue campaign send';
        await supabase.from('email_campaigns').update({ status: 'failed' }).eq('id', campaign.id);
        throw new Error(msg);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Campaign billing retry failed:', err);
      setError(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setProcessing(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100100] flex items-center justify-center p-4 bg-black/50"
      style={{
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        // Keep the card clear of the iOS notch & home indicator
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
      }}
      onClick={() => {
        if (!processing) onClose();
      }}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="campaign-billing-retry-title"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600 shrink-0">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h2
                id="campaign-billing-retry-title"
                className="text-[15px] font-semibold text-gray-900 truncate"
              >
                {alreadyPaid ? 'Retry send' : 'Retry charge & send'}
              </h2>
              <p className="text-[12px] text-gray-500 truncate">{campaign.name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-[13px] text-gray-600 leading-relaxed">
            {alreadyPaid
              ? 'Payment already succeeded for this campaign. Retrying will queue the send without charging again.'
              : 'Payment failed for this email campaign. Retry to charge your card on file, or pay with a different card if that charge fails.'}
          </p>

          <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 space-y-2">
            <div className="flex justify-between text-[13px]">
              <span className="text-gray-500">Recipients</span>
              <span className="font-semibold text-gray-900">
                {recipientCount.toLocaleString()}
              </span>
            </div>
            {!alreadyPaid ? (
              <div className="flex justify-between text-[13px]">
                <span className="text-gray-500">Charge</span>
                <span className="font-semibold text-gray-900">${amount.toFixed(2)}</span>
              </div>
            ) : (
              <div className="flex justify-between text-[13px]">
                <span className="text-gray-500">Payment</span>
                <span className="font-semibold text-emerald-700">Already captured</span>
              </div>
            )}
          </div>

          {error ? (
            <p className="text-[12px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            className="px-4 py-2.5 text-[13px] font-semibold text-gray-700 bg-white border border-gray-200 rounded-[10px] disabled:opacity-50"
            style={{ minHeight: 44 }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleRetry()}
            disabled={processing || recipientCount <= 0}
            className="px-4 py-2.5 text-[13px] font-semibold text-white bg-gray-900 rounded-[10px] disabled:bg-gray-400 flex items-center gap-2"
            style={{ minHeight: 44 }}
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {alreadyPaid ? 'Sending…' : 'Charging…'}
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5" />
                {alreadyPaid ? 'Retry send' : 'Retry charge & send'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
